import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  FormControlLabel,
  Switch,
  Chip,
  Box,
  Typography,
  IconButton,
  Menu,
  FormControl
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import apiClient from "Utils/apiClient";
import { toast } from "react-toastify";

const DriverPlanModal = ({ open, onClose, editData, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: "",
    planType: "SUBSCRIPTION",
    price: 0,
    durationDays: 30,
    description: "",
    features: [],
    isActive: true,
  });
  
  const [featureInput, setFeatureInput] = useState("");
  const [menuAnchor, setMenuAnchor] = useState(null);

  useEffect(() => {
    if (editData) {
      setFormData({
        name: editData.name || "",
        planType: editData.planType || "SUBSCRIPTION",
        price: editData.price || 0,
        durationDays: editData.durationDays || 30,
        description: editData.description || "",
        features: editData.features || [],
        isActive: editData.isActive !== undefined ? editData.isActive : true,
      });
    } else {
      setFormData({
        name: "",
        planType: "SUBSCRIPTION",
        price: 0,
        durationDays: 30,
        description: "",
        features: [],
        isActive: true,
      });
    }
  }, [editData, open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddFeature = () => {
    if (featureInput.trim() !== "") {
      setFormData((prev) => ({
        ...prev,
        features: [...prev.features, featureInput.trim()],
      }));
      setFeatureInput("");
    }
  };

  const handleRemoveFeature = (indexToRemove) => {
    setFormData((prev) => ({
      ...prev,
      features: prev.features.filter((_, index) => index !== indexToRemove),
    }));
  };

  const handleSubmit = async () => {
    if (!formData.name || formData.price < 0 || formData.durationDays <= 0) {
      toast.error("Please fill required fields correctly.");
      return;
    }
    
    try {
      if (editData && editData._id) {
        await apiClient.put(`/driver-plan/${editData._id}`, formData);
        toast.success("Driver plan updated successfully");
      } else {
        await apiClient.post("/driver-plan", formData);
        toast.success("Driver plan created successfully");
      }
      onSubmit();
    } catch (error) {
      toast.error("An error occurred");
      console.error(error);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <IconButton
        edge="end"
        onClick={onClose}
        aria-label="close"
        sx={{ position: "absolute", top: 10, right: 15 }}
      >
        <CloseIcon />
      </IconButton>
      <Box sx={{ padding: 4, display: "flex", flexDirection: "column", gap: 3, backgroundColor: "#f7f9fc" }}>
        <Typography variant="h5" fontWeight="bold">
          {editData ? "Update Driver Plan" : "Add Driver Plan"}
        </Typography>

        <TextField
          fullWidth
          variant="outlined"
          label="Plan Name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
        />

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <Box
                onClick={(e) => setMenuAnchor(e.currentTarget)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  border: "1px solid #c4c4c4",
                  borderRadius: "8px",
                  padding: "13px 14px",
                  cursor: "pointer",
                  backgroundColor: "#fff",
                  position: "relative"
                }}
              >
                <Typography sx={{ position: "absolute", top: -10, left: 10, backgroundColor: "#fff", px: 0.5, fontSize: "0.75rem", color: "rgba(0, 0, 0, 0.6)" }}>
                  Plan Type
                </Typography>
                <Typography>{formData.planType === "SUBSCRIPTION" ? "Subscription" : "Pay Per Escort"}</Typography>
                <Box component="span" sx={{ transform: "scale(0.8)", opacity: 0.6 }}>▼</Box>
              </Box>
              <Menu 
                anchorEl={menuAnchor} 
                open={Boolean(menuAnchor)} 
                onClose={() => setMenuAnchor(null)}
              >
                <MenuItem onClick={() => { setFormData(prev => ({ ...prev, planType: "SUBSCRIPTION" })); setMenuAnchor(null); }}>Subscription</MenuItem>
                <MenuItem onClick={() => { setFormData(prev => ({ ...prev, planType: "PAY_PER_ESCORT" })); setMenuAnchor(null); }}>Pay Per Escort</MenuItem>
              </Menu>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              variant="outlined"
              type="number"
              label="Price (₦)"
              name="price"
              value={formData.price}
              onChange={handleChange}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              variant="outlined"
              type="number"
              label="Duration (Days)"
              name="durationDays"
              value={formData.durationDays}
              onChange={handleChange}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6} display="flex" alignItems="center">
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData((prev) => ({ ...prev, isActive: e.target.checked }))}
                />
              }
              label={<Typography fontWeight="medium">Is Active</Typography>}
            />
          </Grid>
        </Grid>

        <TextField
          fullWidth
          variant="outlined"
          multiline
          rows={3}
          label="Description"
          name="description"
          value={formData.description}
          onChange={handleChange}
        />

        <Box display="flex" flexDirection="column" gap={2}>
          <Box display="flex" gap={2}>
            <TextField
              fullWidth
              variant="outlined"
              label="Add Feature"
              value={featureInput}
              onChange={(e) => setFeatureInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAddFeature();
                }
              }}
            />
            <Button variant="contained" sx={{ color: "#fff" }} onClick={handleAddFeature}>
              Add
            </Button>
          </Box>
          <Box display="flex" gap={1} flexWrap="wrap">
            {formData.features.map((feature, idx) => (
              <Chip key={idx} label={feature} onDelete={() => handleRemoveFeature(idx)} sx={{ px: 1, py: 2 }} />
            ))}
          </Box>
        </Box>

        <Button
          onClick={handleSubmit}
          variant="contained"
          sx={{
            mt: 2,
            width: "50%",
            alignSelf: "center",
            backgroundColor: "#1976d2",
            color: "#ffffff",
            fontWeight: "bold",
            padding: "12px 0",
            fontSize: "16px",
            textTransform: "none",
            borderRadius: "8px",
            "&:hover": {
              backgroundColor: "#1565c0",
            },
          }}
        >
          {editData ? "Update Plan" : "Save Plan"}
        </Button>
      </Box>
    </Dialog>
  );
};

export default DriverPlanModal;
